import PresencaRepresentanteParte from '@/domain/parterepresentante/PresencaRepresentanteParte';
import ProcessoFormatter from '@/domain/processo/ProcessoFormatter';
import SessaoFormatter from '@/domain/sessao/SessaoFormatter';
import EstadoSustentacaoOral from '@/dominio/EstadoSustentacaoOral';
import ImpedimentoBusiness from '@/dominio/ImpedimentoBusiness';
import MeioJulgamentoProcesso from '@/dominio/MeioJulgamentoProcesso';
import ModoParticipacaoAdvogado from '@/dominio/ModoParticipacaoAdvogado';
import ResultadoJulgamento, { RESULTADO_JULGAMENTO } from '@/dominio/ResultadoJulgamento';
import { SITUACAO_IMPEDIMENTOS_PROCESSO } from '@/dominio/SituacaoImpedimentosProcesso';
import { SITUACAO_VOTO } from '@/dominio/SituacaoVoto';
import { TIPO_VOTO } from '@/dominio/TipoVoto';
import VotacaoBusiness from '@/dominio/VotacaoBusiness';
import StringUtil from '@/util/StringUtil';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
    alpha
} from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { blue, grey, lightBlue, red } from '@mui/material/colors';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { Fragment } from 'react';
import '../css/processo.css';
import ReactComponent from './ReactComponent';

const ESPACAMENTO_ENTRE_INFORMACOES = '5px';
const COR_PRIMARIA = lightBlue[700];
const FONTE_DESTAQUE_NEGRITO = 600;

// ============================================================================
// Classe utilitária para formatação de inscrições de representantes
// ============================================================================
class InscricaoRepresentanteUtils {
    static formatDataInscricao(dataSolicitacao) {
        if (!dataSolicitacao) return null;
        const m = dayjs(dataSolicitacao, 'DD-MM-YYYY-HH-mm-ss');
        return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : null;
    }

    static buildDetalhesAdvogado(adv) {
        const detalhes = [];

        const modalidade = adv.modoParticipacao
            ? ModoParticipacaoAdvogado.getDescricaoByCodigo(adv.modoParticipacao)
            : 'Não identificada';

        detalhes.push(`Modalidade: ${modalidade}`);

        const presenca = PresencaRepresentanteParte.getDescricaoPresencaCurtaByCodigo(adv.presente);
        detalhes.push(`Presença: ${presenca}`);

        const estado =
            adv.estadoSustentacaoOral?.descricao ||
            (adv.estadoSustentacaoOral?.id
                ? EstadoSustentacaoOral.getDescricaoByCodigo(adv.estadoSustentacaoOral.id)
                : 'Sem registro');

        detalhes.push(`Situação: ${estado}`);

        return detalhes.length > 0 ? detalhes.join(' | ') : null;
    }

    static renderTooltipIcone(adv) {
        const modalidade = adv.modoParticipacao
            ? ModoParticipacaoAdvogado.getDescricaoByCodigo(adv.modoParticipacao)
            : 'Não identificada';

        const presenca = PresencaRepresentanteParte.getDescricaoPresencaCurtaByCodigo(adv.presente);

        return (
            <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body2">
                    Modalidade: <b>{modalidade}</b>
                </Typography>
                <Typography variant="body2">
                    Presença: <b>{presenca}</b>
                </Typography>
            </Box>
        );
    }

    static formatOab(adv) {
        if (!adv.regOAB?.numero || !adv.regOAB?.siglaUf) return '';
        const letra = adv.regOAB.letra ? `-${adv.regOAB.letra}` : '';
        return ` - OAB: ${adv.regOAB.numero}${letra}/${adv.regOAB.siglaUf}`;
    }

    static renderInscricoes(pedidosDetalhados) {
        if (!pedidosDetalhados?.length) return null;

        return (
            <Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    Detalhes das inscrições de representantes de partes
                </Typography>
                <ul style={{ margin: '4px 0', paddingInlineStart: '30px', listStyleType: 'disc' }}>
                    {pedidosDetalhados.map((pedido) => (
                        <li
                            key={StringUtil.fnv1a32(`${pedido.denominacaoDaParte}|${pedido.nomeDaParte}`)}
                            style={{ marginBottom: '8px' }}
                        >
                            <Typography
                                display="inline"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {pedido.denominacaoDaParte
                                    ? pedido.denominacaoDaParte + ': '
                                    : '(Denominação não informada) '}
                            </Typography>
                            <Typography display="inline">{pedido.nomeDaParte}</Typography>
                            {pedido.advogados?.length > 0 && (
                                <ul style={{ paddingInlineStart: '20px', listStyleType: 'circle', margin: '4px 0' }}>
                                    {pedido.advogados.map((adv) => {
                                        const detalhes = this.buildDetalhesAdvogado(adv);
                                        const corIcone = adv.presente
                                            ? PresencaRepresentanteParte.getCorByCodigo(adv.presente)
                                            : undefined;
                                        const icone = ModoParticipacaoAdvogado.getIconeByCodigo(
                                            adv.modoParticipacao,
                                            '22px',
                                            corIcone
                                        );
                                        return (
                                            <li
                                                key={StringUtil.fnv1a32(adv.nome)}
                                                style={{ marginBottom: detalhes ? '4px' : 0 }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {detalhes ? (
                                                        <Tooltip
                                                            title={this.renderTooltipIcone(adv)}
                                                            arrow
                                                        >
                                                            <span
                                                                style={{ display: 'inline-flex', alignItems: 'center' }}
                                                            >
                                                                {icone}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            {icone}
                                                        </span>
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        display="inline"
                                                    >
                                                        {adv.nome}
                                                        {this.formatOab(adv)}
                                                    </Typography>
                                                </Box>
                                                {detalhes && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ ml: 4, color: 'text.secondary' }}
                                                    >
                                                        {detalhes}
                                                    </Typography>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </Box>
        );
    }
}

// ============================================================================
// Classe utilitária com métodos estáticos para formatação geral das sessões
// ============================================================================
class SessoesAnterioresUtils {
    static getDescricaoSessao(julgamento) {
        if (!julgamento?.detalheSessaoAnterior) {
            return 'Sessão não identificada';
        }

        const sessao = julgamento.detalheSessaoAnterior;
        let descricao = sessao.orgaoJudicante?.sigla ? `${sessao.orgaoJudicante.sigla} - ` : '';
        descricao += sessao.tipo ? `${sessao.tipo} - ` : '';

        const exibirHora = sessao.tipoJulgamento !== 'VIRTUAL';

        descricao += SessaoFormatter.formatarDataSessao(sessao.tipoJulgamento, sessao.prazos, exibirHora);

        return descricao;
    }

    static getOpcaoLabel(julgamento) {
        const data = julgamento.dataJulgamento
            ? moment(julgamento.dataJulgamento, 'DD-MM-YYYY').format('DD/MM/YYYY')
            : '(SEM DATA)';
        const resultado = julgamento.descricaoResultadoJulgamento || '(sem resultado)';
        return `${data} - ${resultado}`;
    }

    static getCorResultadoJulgamento(codigoResultadoJulgamento) {
        let corTexto = '#000000';
        let corFundo = 'transparent';

        if (codigoResultadoJulgamento) {
            corTexto = ResultadoJulgamento.getCorByCodigo(codigoResultadoJulgamento) || '#000000';
            corFundo = alpha(corTexto, 0.2);
        }

        return { corTexto, corFundo };
    }

    static renderIconeMeioJulgamentoProcesso(julgamento, comTooltip) {
        if (!julgamento?.detalheSessaoAnterior) {
            return null;
        }

        const sessao = julgamento.detalheSessaoAnterior;
        const codigo = sessao.processos?.[0]?.tipoJulgamento || '';
        const icone = MeioJulgamentoProcesso.getIconeByCodigo(codigo, '22px');

        if (comTooltip) {
            return (
                <Tooltip
                    title={
                        <Typography sx={{ fontSize: '0.7rem' }}>
                            {MeioJulgamentoProcesso.getDescricaoCurtaByCodigo(codigo) ||
                                'Meio de julgamento desconhecido'}
                        </Typography>
                    }
                    placement="right"
                >
                    {icone}
                </Tooltip>
            );
        }
        return icone;
    }

    static votoDoMagistrado(magistrado, votos) {
        if (!votos) {
            return null;
        }

        return votos.find((v) => magistrado?.sigla && v?.siglaDoMagistrado && magistrado.sigla === v.siglaDoMagistrado);
    }

    static isImpedidoNaComposicao(composicao, impedimentos, sigla) {
        return ImpedimentoBusiness.isImpedidoNaComposicao(composicao, impedimentos, sigla);
    }

    static isPossivelmenteImpedidoNaComposicao(composicao, impedimentos, sigla) {
        return ImpedimentoBusiness.isPossivelmenteImpedidoNaComposicao(composicao, impedimentos, sigla);
    }

    static getDetalhesNaoVotante(processo, composicao, impedimentos, magistrado) {
        if (!magistrado?.sigla) {
            return null;
        }

        return {
            relator: magistrado.sigla === processo?.relator?.sigla,
            ausente: magistrado.ausente || false,
            impedido: this.isImpedidoNaComposicao(composicao, impedimentos, magistrado.sigla),
            possivelmenteImpedido: this.isPossivelmenteImpedidoNaComposicao(composicao, impedimentos, magistrado.sigla)
        };
    }

    static getConfigVotoExistente(detalhesVoto) {
        if (!detalhesVoto.podeExibir) {
            return {
                descricao: detalhesVoto.descricao,
                corTexto: SITUACAO_VOTO.SEM_VOTO.cor,
                corBorda: grey[900],
                fontWeight: FONTE_DESTAQUE_NEGRITO
            };
        }

        const descricao =
            detalhesVoto.descricao + (detalhesVoto.situacao?.descricao ? ` (${detalhesVoto.situacao.descricao})` : '');

        const configs = {
            [SITUACAO_VOTO.CONFIRMADO.id]: {
                corTexto: detalhesVoto.cor,
                corBorda: SITUACAO_VOTO.CONFIRMADO.cor,
                fontWeight: FONTE_DESTAQUE_NEGRITO
            },
            [SITUACAO_VOTO.CONFIRMADO_TACITAMENTE.id]: {
                corTexto: detalhesVoto.cor,
                corBorda: SITUACAO_VOTO.CONFIRMADO.cor,
                fontWeight: FONTE_DESTAQUE_NEGRITO
            },
            [SITUACAO_VOTO.REVOGADO.id]: {
                corTexto: SITUACAO_VOTO.SEM_VOTO.cor,
                corBorda: SITUACAO_VOTO.REVOGADO.cor,
                fontWeight: FONTE_DESTAQUE_NEGRITO - 100
            },
            [SITUACAO_VOTO.PRE_VOTADO.id]: {
                corTexto: SITUACAO_VOTO.SEM_VOTO.cor,
                corBorda: SITUACAO_VOTO.PRE_VOTADO.cor,
                fontWeight: FONTE_DESTAQUE_NEGRITO - 100
            },
            [SITUACAO_VOTO.NAO_CONFIRMADO_PRESENCIAL.id]: {
                corTexto: detalhesVoto.cor,
                corBorda: SITUACAO_VOTO.NAO_CONFIRMADO_PRESENCIAL.cor,
                fontWeight: FONTE_DESTAQUE_NEGRITO
            }
        };
        return {
            descricao,
            ...(configs[detalhesVoto.situacao?.id] || { corTexto: '#000000', corBorda: '#000000', fontWeight: 400 })
        };
    }

    static getConfigNaoVotante(processo, composicao, impedimentos, magistrado) {
        const d = this.getDetalhesNaoVotante(processo, composicao, impedimentos, magistrado);
        const FONTE_NORMAL = 400;

        if (d?.relator)
            return {
                descricao: 'Relator',
                corTexto: blue[700],
                corBorda: blue[900],
                fontWeight: FONTE_DESTAQUE_NEGRITO
            };
        if (d?.ausente)
            return {
                descricao: 'Ausente na sessão',
                corTexto: TIPO_VOTO.AUSENTE.cor,
                corBorda: red[900],
                fontWeight: FONTE_NORMAL
            };
        if (d?.impedido)
            return {
                descricao: 'Impedido',
                corTexto: SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_NA_COMPOSICAO.cor,
                corBorda: red[900],
                fontWeight: FONTE_NORMAL
            };
        if (d?.possivelmenteImpedido)
            return {
                descricao: 'Possivelmente impedido',
                corTexto: SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_NA_COMPOSICAO.cor,
                corBorda: red[900],
                fontWeight: FONTE_NORMAL
            };
        return {
            descricao: 'Sem registro de voto na sessão',
            corTexto: SITUACAO_VOTO.SEM_VOTO.cor,
            corBorda: grey[600],
            fontWeight: FONTE_NORMAL
        };
    }

    static getInfoVisualVoto(detalhesSessao, detalhesJulgamento, magistrado) {
        const voto = this.votoDoMagistrado(magistrado, detalhesJulgamento?.votos);
        const meioJulgamentoProcesso = detalhesSessao?.processos
            ? detalhesSessao.processos[0]?.tipoJulgamento
            : undefined;
        const detalhesVoto = VotacaoBusiness.getObjDetalhesVoto(voto, meioJulgamentoProcesso);
        const config = detalhesVoto
            ? this.getConfigVotoExistente(detalhesVoto)
            : this.getConfigNaoVotante(
                  detalhesJulgamento?.processo,
                  detalhesJulgamento?.composicao,
                  detalhesJulgamento?.impedimentos,
                  magistrado
              );
        config.corFundo = config.corTexto ? alpha(config.corTexto, 0.2) : 'transparent';
        return config;
    }

    static getDescricaoResultadoJulgamentoAnterior(salaSessaoStore, julgamentoAnterior) {
        const tipo = salaSessaoStore.getTipoResultadoJulgamento(julgamentoAnterior.tipoResultadoJulgamento);
        return tipo ? tipo.descricao : '';
    }
}

// ============================================================================
// Subcomponente: JulgamentoAnteriorDetalhes
// ============================================================================
const JulgamentoAnteriorDetalhes = observer(({ julgamento, exibirDetalhesInscricoes }) => {
    if (!julgamento) return null;
    const corResultado = SessoesAnterioresUtils.getCorResultadoJulgamento(julgamento.tipoResultadoJulgamento);

    const detalhe = julgamento.detalheJulgamentoAnterior;
    const temPedido = detalhe?.pedidoSustentacaoOral;
    const temInscricoesDetalhadas = exibirDetalhesInscricoes && temPedido?.pedidosDetalhados?.length > 0;

    return (
        <Card
            variant="outlined"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <CardHeader
                title={
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: FONTE_DESTAQUE_NEGRITO }}
                        >
                            {SessoesAnterioresUtils.getDescricaoSessao(julgamento)}
                        </Typography>
                        <Chip
                            label={julgamento.detalheSessaoAnterior?.descricao || julgamento.sessaoId}
                            size="small"
                            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.9em', paddingX: 1 } }}
                        />
                    </Stack>
                }
                sx={{ bgcolor: alpha(lightBlue[500], 0.05), py: 1, px: 2 }}
            />
            <CardContent sx={{ p: 2, flexGrow: 1 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                        >
                            Fase
                        </Typography>
                        <Typography variant="body2">
                            {julgamento.numeroProcesso?.split(' - ')[0] || 'Indisponível'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                        >
                            Resultado
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ display: 'inline-flex' }}
                        >
                            <Tooltip
                                title={
                                    <Typography sx={{ fontSize: '0.7rem' }}>
                                        {ProcessoFormatter.formatarDataJulgamentoProcesso(
                                            julgamento.detalheJulgamentoAnterior?.dataJulgamento ||
                                                julgamento.dataJulgamento
                                        ) || 'Data indisponível'}
                                    </Typography>
                                }
                                placement="right"
                                slotProps={{
                                    popper: { modifiers: [{ name: 'offset', options: { offset: [0, 30] } }] }
                                }}
                            >
                                <Chip
                                    label={julgamento.descricaoResultadoJulgamento || 'Indisponível'}
                                    size="small"
                                    sx={{
                                        bgcolor: corResultado.corFundo,
                                        color: corResultado.corTexto,
                                        fontWeight: FONTE_DESTAQUE_NEGRITO
                                    }}
                                />
                            </Tooltip>
                            {SessoesAnterioresUtils.renderIconeMeioJulgamentoProcesso(julgamento, true)}
                        </Stack>
                    </Box>

                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                        >
                            Inscrições de representantes?
                        </Typography>
                        {temPedido ? (
                            <Chip
                                label="Sim"
                                size="small"
                                color="primary"
                            />
                        ) : (
                            <Chip
                                label="Não"
                                size="small"
                                color="secondary"
                            />
                        )}
                    </Box>

                    {julgamento.detalheJulgamentoAnterior?.informacoesAdicionais?.VistaRegimental && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                            >
                                Vistas Regimentais
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                            >
                                {julgamento.detalheJulgamentoAnterior.informacoesAdicionais.VistaRegimental.map(
                                    (vista) => (
                                        <Chip
                                            key={vista.sigla}
                                            label={vista.sigla}
                                            size="medium"
                                            sx={{
                                                bgcolor: alpha(RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.cor, 0.1),
                                                color: RESULTADO_JULGAMENTO.VISTA_REGIMENTAL.cor,
                                                fontWeight: FONTE_DESTAQUE_NEGRITO
                                            }}
                                        />
                                    )
                                )}
                            </Stack>
                        </Box>
                    )}
                    {julgamento.detalheJulgamentoAnterior?.composicao && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                            >
                                Composição / Votação
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                            >
                                {julgamento.detalheJulgamentoAnterior.composicao.map((composicao) => {
                                    const info = SessoesAnterioresUtils.getInfoVisualVoto(
                                        julgamento.detalheSessaoAnterior,
                                        julgamento.detalheJulgamentoAnterior,
                                        composicao?.magistrado
                                    );
                                    return (
                                        <Tooltip
                                            key={composicao.magistrado.sigla}
                                            title={info.descricao}
                                        >
                                            <Chip
                                                label={`${composicao.magistrado.sigla}${composicao.magistrado.presidenteSessao ? ' (P)' : ''}`}
                                                size="medium"
                                                variant="filled"
                                                sx={{
                                                    color: info.corTexto,
                                                    fontWeight: info.fontWeight,
                                                    bgcolor: info.corFundo,
                                                    borderWidth: '3px',
                                                    borderStyle: 'solid',
                                                    borderColor: info.corBorda
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                    {julgamento.detalheJulgamentoAnterior?.informacaoProcessoSalaSessao?.complementoAnotacoes && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                gutterBottom
                            >
                                Anotações do secretário
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{ p: 1.5, bgcolor: alpha(grey[500], 0.05), borderRadius: 1 }}
                            >
                                <Typography variant="body2">
                                    {
                                        julgamento.detalheJulgamentoAnterior.informacaoProcessoSalaSessao
                                            .complementoAnotacoes
                                    }
                                </Typography>
                            </Paper>
                        </Box>
                    )}

                    {temInscricoesDetalhadas && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            {InscricaoRepresentanteUtils.renderInscricoes(temPedido.pedidosDetalhados)}
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
});

// ============================================================================
// Componente principal
// ============================================================================
class JulgamentosAnteriores extends ReactComponent {
    abrirDialog = false;
    selectedJulgamento = null;
    loadingDetails = false;

    constructor(props) {
        super(props);
        makeObservable(this, {
            abrirDialog: observable,
            selectedJulgamento: observable,
            loadingDetails: observable,
            registro: computed,
            julgamentosAnteriores: computed,
            existeJulgamentosAnteriores: computed,
            abrirJulgamentoAnterior: action,
            handleCloseDialog: action,
            handleSelectChange: action,
            loadJulgamentoDetails: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }
    get julgamentosAnteriores() {
        return this.registro.detalhes?.julgamentosAnteriores;
    }
    get existeJulgamentosAnteriores() {
        return this.julgamentosAnteriores && this.julgamentosAnteriores.length > 0;
    }

    loadJulgamentoDetails = async (julgamento) => {
        if (julgamento.detalheSessaoAnterior) return julgamento;
        this.loadingDetails = true;

        try {
            const resposta = await this.sessaoService.recuperarSessaoComProcesso(
                julgamento.sessaoId,
                julgamento.numeroProcesso
            );
            runInAction(() => {
                julgamento.detalheSessaoAnterior = resposta;
                julgamento.detalheJulgamentoAnterior = resposta.processos[0];
                this.loadingDetails = false;
                // Força atualização do selectedJulgamento para re-renderizar o select
                this.selectedJulgamento = { ...julgamento };
            });
            return julgamento;
        } catch (error) {
            console.error('Erro ao carregar detalhes:', error);
            this.mensagemStore.abrirMensagemErro('Erro ao carregar detalhes da sessão anterior.');
            runInAction(() => {
                this.loadingDetails = false;
            });
            throw error;
        }
    };

    abrirJulgamentoAnterior = async () => {
        const promises = this.julgamentosAnteriores
            .filter((j) => !j.detalheSessaoAnterior)
            .map((j) => this.sessaoService.recuperarSessaoComProcesso(j.sessaoId, j.numeroProcesso));

        if (promises.length > 0) {
            try {
                const respostas = await Promise.all(promises);
                runInAction(() => {
                    respostas.forEach((resposta) => {
                        const j = this.julgamentosAnteriores.find((j) => j.sessaoId === resposta.id);

                        if (j) {
                            j.detalheSessaoAnterior = resposta;
                            j.detalheJulgamentoAnterior = resposta.processos[0];
                        }
                    });
                    this.abrirDialog = true;
                });
            } catch (error) {
                console.error('Erro ao carregar detalhes:', error);
                this.mensagemStore.abrirMensagemErro('Erro ao carregar detalhes de sessões anteriores.');
                runInAction(() => {
                    this.abrirDialog = true;
                });
            }
        } else {
            runInAction(() => {
                this.abrirDialog = true;
            });
        }
    };

    handleCloseDialog = () => {
        this.abrirDialog = false;
    };

    handleSelectChange = async (event) => {
        const sessaoId = event.target.value;

        if (!sessaoId) {
            this.selectedJulgamento = null;
            return;
        }

        const julgamento = this.julgamentosAnteriores.find((j) => j.sessaoId === sessaoId);

        if (julgamento) {
            this.selectedJulgamento = julgamento;

            if (!julgamento.detalheSessaoAnterior) {
                await this.loadJulgamentoDetails(julgamento);
            }
        }
    };

    get labelDialog() {
        const prefix =
            this.existeJulgamentosAnteriores && this.julgamentosAnteriores.length > 1
                ? 'Julgamentos anteriores '
                : 'Julgamento anterior ';
        return prefix + this.registro.processo.numeroCompleto;
    }

    renderDialog() {
        if (!this.abrirDialog) return null;
        return (
            <Dialog
                onClose={this.handleCloseDialog}
                open={this.abrirDialog}
                maxWidth="lg"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: 2, maxHeight: '90vh' } } }}
            >
                <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', py: 2, px: 3 }}>
                    <Typography
                        variant="h6"
                        component="span"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                    >
                        {this.labelDialog}
                    </Typography>
                </DialogTitle>
                <Box sx={{ p: 3, overflow: 'auto' }}>
                    <Grid
                        container
                        spacing={3}
                    >
                        {this.julgamentosAnteriores.map((j) => (
                            <Grid
                                size={{ xs: 12, md: 6 }}
                                key={j.sessaoId}
                            >
                                <JulgamentoAnteriorDetalhes
                                    julgamento={j}
                                    exibirDetalhesInscricoes={this.observableProps.exibirDetalhesInscricoes}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                        variant="contained"
                        onClick={this.handleCloseDialog}
                        endIcon={<CloseIcon />}
                        sx={{ bgcolor: COR_PRIMARIA, '&:hover': { bgcolor: lightBlue[800] } }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    renderCaixaSelecaoParaExibirUmaPorUma() {
        const { julgamentosAnteriores } = this;
        const { selectedJulgamento, loadingDetails } = this;

        // Seleção automática se houver apenas um
        if (julgamentosAnteriores.length === 1 && !selectedJulgamento) {
            runInAction(() => {
                this.selectedJulgamento = julgamentosAnteriores[0];
            });

            if (!julgamentosAnteriores[0].detalheSessaoAnterior) {
                this.loadJulgamentoDetails(julgamentosAnteriores[0]);
            }
        }

        // Função que define o texto mostrado no campo do select
        const renderSelectedValue = (selectedId) => {
            if (!selectedId || !selectedJulgamento) return '';

            if (selectedJulgamento.detalheSessaoAnterior) {
                const descricaoSessao = SessoesAnterioresUtils.getDescricaoSessao(selectedJulgamento);
                const resultado = selectedJulgamento.descricaoResultadoJulgamento || '';
                return `${descricaoSessao} - ${resultado}`;
            }

            return SessoesAnterioresUtils.getOpcaoLabel(selectedJulgamento);
        };

        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                <Typography
                    color={lightBlue[600]}
                    sx={{ mb: 1, fontWeight: 'bold' }}
                >
                    {julgamentosAnteriores.length > 1 ? 'Selecione a sessão anterior:' : 'Sessão anterior:'}
                </Typography>

                {julgamentosAnteriores.length > 1 && (
                    <Select
                        value={selectedJulgamento?.sessaoId || ''}
                        onChange={this.handleSelectChange}
                        displayEmpty
                        fullWidth
                        size="small"
                        renderValue={renderSelectedValue}
                    >
                        <MenuItem
                            value=""
                            disabled
                        >
                            Escolha uma sessão
                        </MenuItem>
                        {julgamentosAnteriores.map((j) => {
                            const opcaoTexto = j.detalheSessaoAnterior
                                ? `${SessoesAnterioresUtils.getDescricaoSessao(j)} - ${j.descricaoResultadoJulgamento || ''}`
                                : SessoesAnterioresUtils.getOpcaoLabel(j);
                            return (
                                <MenuItem
                                    key={j.sessaoId}
                                    value={j.sessaoId}
                                >
                                    {opcaoTexto}
                                </MenuItem>
                            );
                        })}
                    </Select>
                )}

                {loadingDetails && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loadingDetails &&
                    selectedJulgamento &&
                    (this.observableProps.renderDetails ? (
                        this.observableProps.renderDetails(selectedJulgamento)
                    ) : (
                        <Box sx={{ mt: 3 }}>
                            <JulgamentoAnteriorDetalhes
                                julgamento={selectedJulgamento}
                                exibirDetalhesInscricoes={this.observableProps.exibirDetalhesInscricoes}
                            />
                        </Box>
                    ))}
            </Box>
        );
    }

    renderTodasSessoesAnteriores() {
        return (
            <Box sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES }}>
                {this.renderDialog()}
                <Typography
                    color={lightBlue[600]}
                    sx={{ mb: ESPACAMENTO_ENTRE_INFORMACOES, fontWeight: 'bold' }}
                >
                    {this.julgamentosAnteriores.length > 1 ? 'Sessões anteriores:' : 'Sessão anterior:'}
                </Typography>
                <Tooltip
                    title={<Typography>Ver detalhes</Typography>}
                    placement="right"
                    slotProps={{
                        popper: {
                            modifiers: [{ name: 'flip', options: { fallbackPlacements: ['right', 'top', 'bottom'] } }]
                        }
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: alpha(lightBlue[500], 0.04) }
                        }}
                        onClick={this.abrirJulgamentoAnterior}
                    >
                        {this.julgamentosAnteriores.map((julgamento, index) => (
                            <Fragment key={julgamento.sessaoId}>
                                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
                                    <EventAvailableIcon sx={{ color: COR_PRIMARIA, fontSize: 20, mr: 1 }} />
                                    {this.renderJulgamentoAnterior(julgamento)}
                                </Box>
                                {index < this.julgamentosAnteriores.length - 1 && <Divider sx={{ ml: 6 }} />}
                            </Fragment>
                        ))}
                    </Paper>
                </Tooltip>
            </Box>
        );
    }

    // Métodos auxiliares para a exibição resumida
    getDescricaoResultadoJulgamentoAnterior(j) {
        return SessoesAnterioresUtils.getDescricaoResultadoJulgamentoAnterior(this.salaSessaoStore, j);
    }

    tooltipMovimento = (movimento) => {
        if (!movimento) return null;
        let descricao = movimento.descricao
            ? this.corrigirString(movimento.descricao)
            : `<span style="color: orange">"O movimento ${movimento.numeroMovimentoCnj} não está disponível."</span>`;

        if (movimento.complementos?.length) {
            descricao = this.dominioStore.substituirDescricoesGruposComplemento(descricao, movimento);
            descricao = this.dominioStore.substituirDescricoesComplementos(
                descricao,
                movimento,
                movimento.complementos
            );
        }
        const html = DOMPurify.sanitize(`${movimento.numeroMovimentoCnj} - ${descricao}`);
        return (
            <div
                style={{ fontSize: '14px' }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    };

    renderJulgamentoAnterior(j) {
        return (
            <li style={{ color: 'black', textAlign: 'left', lineHeight: 1.5, listStyleType: 'none' }}>
                <Typography display="inline">
                    {j.dataJulgamento ? moment(j.dataJulgamento, 'DD-MM-YYYY').format('DD/MM/YYYY') : '(SEM DATA)'}
                </Typography>
                <Typography display="inline">{` / ${j.numeroProcesso.split(' - ')[0]}`}</Typography>
                <Typography display="inline">{` / ${this.getDescricaoResultadoJulgamentoAnterior(j)}`}</Typography>
                {this.renderMovimentos(j)}
                {this.renderDecisao(j)}
            </li>
        );
    }

    renderDecisao(j) {
        if (!j?.decisao?.id) return null;
        return (
            <>
                <Typography
                    display="inline"
                    noWrap
                >
                    {' / Decisão: '}
                </Typography>
                <Tooltip
                    title={<Typography noWrap>{j.decisao.descricao}</Typography>}
                    placement="right"
                >
                    <Typography
                        display="inline"
                        noWrap
                    >
                        {j.decisao.id}
                    </Typography>
                </Tooltip>
            </>
        );
    }

    renderMovimentos(j) {
        if (!j?.movimentos?.length) return null;
        return (
            <>
                <Typography
                    display="inline"
                    noWrap
                >{` / ${j.movimentos.length === 1 ? 'Movimento' : 'Movimentos'}: `}</Typography>
                {j.movimentos.map((mov, idx) => (
                    <Tooltip
                        key={this.nextKey}
                        title={this.tooltipMovimento(mov)}
                        placement="top-end"
                    >
                        <Typography
                            display="inline"
                            noWrap
                        >
                            {mov.numeroMovimentoCnj + (idx < j.movimentos.length - 1 ? '; ' : '')}
                        </Typography>
                    </Tooltip>
                ))}
            </>
        );
    }

    render() {
        if (!this.existeJulgamentosAnteriores) {
            return null;
        }

        const { variant = 'dialog' } = this.props;
        return variant === 'select'
            ? this.renderCaixaSelecaoParaExibirUmaPorUma()
            : this.renderTodasSessoesAnteriores();
    }
}

export default inject('stores')(observer(JulgamentosAnteriores));
